import { OperationQueue } from '@research-assistant/shared';

import { initializeIdentity, _resetIdentityForTests } from '../identity.js';
import { logger } from '../logger.js';
import { setIdentity, setQueueSize } from '../store/identity-store.js';

/**
 * Client-side operation queue. Mirrors the server's sequential discipline:
 * every user-triggered action is enqueued, processed one at a time, and the
 * caller's Promise resolves with the action's own result.
 *
 * Phase 3 ships a single variant (`refresh-identity`) so we exercise the
 * primitive end-to-end. Phase 4 extends this union with message ops.
 */
export type ClientAction = { type: 'refresh-identity' };

export type ClientActionResult = { type: 'refresh-identity'; stale: boolean };

/**
 * Internal dispatcher. Discriminated-union handler keeps each action's
 * result type narrowable without casting at the call site.
 */
async function handleAction(action: ClientAction): Promise<ClientActionResult> {
  switch (action.type) {
    case 'refresh-identity': {
      // Force a fresh resolve by clearing the singleton, then refreshing
      // the store with the outcome.
      _resetIdentityForTests();
      const identity = await initializeIdentity();
      setIdentity(identity.me, identity.stale);
      return { type: 'refresh-identity', stale: identity.stale };
    }
    default: {
      // Exhaustiveness check. `never` here guarantees Phase 4 additions
      // cannot silently skip the switch.
      const exhaustive: never = action.type;
      throw new Error(`Unhandled ClientAction variant: ${String(exhaustive)}`);
    }
  }
}

export class ClientOperationQueue {
  private readonly queue: OperationQueue<ClientAction, ClientActionResult>;

  constructor(
    handler: (
      action: ClientAction,
    ) => Promise<ClientActionResult> = handleAction,
  ) {
    this.queue = new OperationQueue<ClientAction, ClientActionResult>(
      async (item) => {
        try {
          return await handler(item);
        } finally {
          // queue.size() excludes the currently-running item; reflect the
          // post-handler depth in the store.
          setQueueSize(this.queue.size());
        }
      },
    );
  }

  enqueue(action: ClientAction): Promise<ClientActionResult> {
    const promise = this.queue.enqueue(action);
    setQueueSize(this.queue.size());
    return promise;
  }

  size(): number {
    return this.queue.size();
  }

  drain(): Promise<void> {
    return this.queue.drain();
  }
}

/**
 * Module-level singleton — the UI subscribes to `setQueueSize` changes via
 * the identity store to render the "Queue: {n}" indicator.
 */
let instance: ClientOperationQueue | null = null;

export function getClientOperationQueue(): ClientOperationQueue {
  if (instance === null) {
    instance = new ClientOperationQueue();
    logger.debug('ClientOperationQueue initialised');
  }
  return instance;
}

/** Test-only helper. */
export function _resetClientOperationQueueForTests(): void {
  instance = null;
}
